import React, { useEffect, useState } from "react"
import {
  WingsModal,
  WingsTextInput,
  WingsTextArea,
  WingsSelectInput,
} from "@wingspans-ui"
import { FormattedMessage, useIntl } from "react-intl"
import { sendStandardToast } from "../toasts/standard-toast"
import useMilestoneCategories from "./useMilestoneCategories"
import { allowedMonths } from "../careers/AllowedTypes"
import { renderMonthName } from "../resume/utils/utils"
import "./UpdateCareerPlanModal.css"

const defaultMilestone = {
  category: "",
  description: "",
  organization: "",
  completed: false,
  startDate: "0000-00",
  endDate: "0000-00",
}

const parseMilestoneDate = (dateString) => {
  const [year, month] = dateString.split("-")
  return {
    year: /^\d{4}$/.test(year) && year !== "0000" ? year : "",
    month: allowedMonths.includes(month) ? month : "",
  }
}

/**
 * Modal for adding/editing a milestone
 * @param {boolean} show
 * @param {Object} milestoneToEdit - the saved version of the milestone being edited
 * @param {Function} onClose
 * @param {Function} onSave - takes in a edited version of the milestone to save, or null to delete it. returns an error message if an error occurs
 * @returns {JSX}
 */
const UpdateMilestoneModal = ({ show, milestoneToEdit, onClose, onSave }) => {
  const { categories } = useMilestoneCategories()
  const { formatMessage, locale } = useIntl()
  const [editedMilestone, setEditedMilestone] = useState({...defaultMilestone})
  const [startMonth, setStartMonth] = useState("")
  const [startYear, setStartYear] = useState("")
  const [endMonth, setEndMonth] = useState("")
  const [endYear, setEndYear] = useState("")

  const handleShow = () => {
    const initialMilestone = {...defaultMilestone, ...(milestoneToEdit || {})}
    setEditedMilestone(initialMilestone)
    const startDate = parseMilestoneDate(initialMilestone.startDate)
    const endDate = parseMilestoneDate(initialMilestone.endDate)
    setStartMonth(startDate.month)
    setStartYear(startDate.year)
    setEndMonth(endDate.month)
    setEndYear(endDate.year)
  }

  useEffect(() => {
    if (show) {
      handleShow()
    }
  }, [show, milestoneToEdit])

  const handleSave = async () => {
    if ((startMonth && !startYear) || (endMonth && !endYear) || (startYear && startYear.length !== 4) || (endYear && endYear.length !== 4)) {
      sendStandardToast({heading: "Any dates must include a 4-digit year"})
      return
    }
    const updatedMilestone = {
      ...editedMilestone,
      startDate: `${startYear || "0000"}-${startMonth || "00"}`,
      endDate: `${endYear || "0000"}-${endMonth || "00"}`,
    }
    const errorMessage = await onSave(updatedMilestone)
    if (errorMessage) {
      sendStandardToast({heading: "Error when trying to save milestone", message: errorMessage})
    } else {
      onClose()
    }
  }

  const isNew = !editedMilestone.id

  const categoryDropdownOptions = categories.map(category => ({
    key: category.slug,
    value: category.slug,
    text: category.title,
  }))

  const monthOptions = allowedMonths.map((monthCode) => ({
    key: monthCode,
    value: monthCode,
    text: renderMonthName(monthCode, locale),
  }))

  return (
    <WingsModal
      isOpen={show}
      onClose={onClose}
      size="md"
      className="update-career-plan-modal"
      title={isNew ? (
        <FormattedMessage id="career-plans-add-milestone" defaultMessage="Add Milestone" />
      ) : (
        <FormattedMessage id="career-plans-edit-milestone" defaultMessage="Edit Milestone" />
      )}
      titleLevel={5}
      headerAlign="left"
      secondaryActionLabel={<FormattedMessage id="save-career-plan-modal-cancel" defaultMessage="Cancel" />}
      onSecondaryAction={onClose}
      primaryActionLabel={<FormattedMessage id="save-career-plan-modal-save" defaultMessage="Save" />}
      onPrimaryAction={handleSave}
      primaryActionDisabled={!editedMilestone.category}
    >
      <WingsModal.Body className="w-flex w-flex-column w-items-start w-gap-1">
        <WingsSelectInput
          label={`${formatMessage({ id: "save-milestone-modal-category", defaultMessage: "Category" })} *`}
          id="milestone-category-select"
          upward={false}
          fullWidth={true}
          options={categoryDropdownOptions}
          value={editedMilestone.category}
          onChange={(value) => setEditedMilestone({ ...editedMilestone, category: value })}
          placeholder=""
          search={false}
        />
        <WingsTextArea
          label={formatMessage({id: "save-milestone-modal-description", defaultMessage: "Description"})}
          id="milestone-description"
          fullWidth={true}
          value={editedMilestone.description}
          onChange={(e) => setEditedMilestone({...editedMilestone, description: e.target.value})}
          autoResize
          autoFocus
        />
        <WingsTextInput
          label={formatMessage({id: "save-milestone-modal-organization", defaultMessage: "Organization"})}
          id="milestone-organization"
          type="text"
          fullWidth={true}
          value={editedMilestone.organization}
          onChange={(e) => setEditedMilestone({...editedMilestone, organization: e.target.value})}
        />
        <div className="update-career-plan-date-group">
          <label htmlFor="milestone-start-date-month">
            <FormattedMessage id="save-milestone-modal-start-date" defaultMessage="Start Date" />
          </label>
          <div className="update-career-plan-date-row">
            <WingsSelectInput
              id="milestone-start-date-month"
              className="update-career-plan-date-month"
              fullWidth={true}
              options={monthOptions}
              value={startMonth}
              onChange={(value) => setStartMonth(value || "")}
              placeholder={formatMessage({ id: "experience-date-input-month-placeholder", defaultMessage: "Month" })}
              clearable={true}
              search={false}
              aria-label={formatMessage({ id: "experience-date-input-month-placeholder", defaultMessage: "Month" })}
            />
            <WingsTextInput
              id="milestone-start-date-year"
              className="update-career-plan-date-year"
              type="text"
              fullWidth={true}
              inputMode="numeric"
              maxLength={4}
              value={startYear}
              placeholder={formatMessage({ id: "experience-dates-input-year", defaultMessage: "Year" })}
              onChange={(e) => setStartYear(e.target.value.replace(/[^0-9]/g, ""))}
              aria-label={formatMessage({ id: "experience-dates-input-year", defaultMessage: "Year" })}
            />
          </div>
        </div>
        <div className="update-career-plan-date-group">
          <label htmlFor="milestone-end-date-month">
            <FormattedMessage id="save-milestone-modal-end-date" defaultMessage="End Date" />
          </label>
          <div className="update-career-plan-date-row">
            <WingsSelectInput
              id="milestone-end-date-month"
              className="update-career-plan-date-month"
              fullWidth={true}
              options={monthOptions}
              value={endMonth}
              onChange={(value) => setEndMonth(value || "")}
              placeholder={formatMessage({ id: "experience-date-input-month-placeholder", defaultMessage: "Month" })}
              clearable={true}
              search={false}
              aria-label={formatMessage({ id: "experience-date-input-month-placeholder", defaultMessage: "Month" })}
            />
            <WingsTextInput
              id="milestone-end-date-year"
              className="update-career-plan-date-year"
              type="text"
              fullWidth={true}
              inputMode="numeric"
              maxLength={4}
              value={endYear}
              placeholder={formatMessage({ id: "experience-dates-input-year", defaultMessage: "Year" })}
              onChange={(e) => setEndYear(e.target.value.replace(/[^0-9]/g, ""))}
              aria-label={formatMessage({ id: "experience-dates-input-year", defaultMessage: "Year" })}
            />
          </div>
        </div>
      </WingsModal.Body>
    </WingsModal>
  )
}

export default UpdateMilestoneModal
